import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  AddToStageData,
  AppData,
  UnifiedWidget,
  PresetEditorPresence,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorCode,
  createAddUnifiedComponentsError,
} from '../errors';
import {
  CompRef,
  ComponentLayoutObject,
  Point,
  WidgetDescriptor,
  CompStructure,
} from '@wix/document-services-types';
import { getAppDescriptorByAppData } from '@wix/blocks-widget-services/editor';
import { addBuilderComponent } from './addBuilderComponent';
import { isBuilderComponentType } from '../../../../utils/builderServices';
import { cleanPresetIdPrefix, getSizeValue, getUnitSize } from '../utils';
import { WidgetDimensions } from '@wix/editor-platform-sdk-types';

const appDescriptors: Record<
  string,
  Awaited<ReturnType<typeof getAppDescriptorByAppData>>
> = {};

const TOKEN = 'unified-components-installation';

function getPresetAndSize(
  widgetId: string,
  isStretched: boolean,
  {
    presets = [],
    defaultSize,
  }: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
  presetId?: string,
  mobilePresetId?: string,
) {
  const preset =
    presets.find(
      (widgetPreset) => cleanPresetIdPrefix(widgetPreset.presetId) === presetId,
    ) ?? presets[0];
  if (!preset) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBlocksWidgetFailed,
      'Could not fetch preset for blocks widget',
    ).withUserComponentId(widgetId, 'unified-widget');
  }

  const size = (preset.defaultSize as WidgetDimensions) ?? defaultSize;
  if (!size) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBlocksWidgetFailed,
      'Could not fetch default size for blocks widget',
    ).withUserComponentId(widgetId, 'unified-widget');
  }

  const mobilePreset =
    presets.find(
      (widgetPreset) =>
        cleanPresetIdPrefix(widgetPreset.presetId) === mobilePresetId,
    ) ?? preset;
  const mobilePresetSize =
    (mobilePreset.defaultSize as WidgetDimensions) ?? size;

  return {
    size: {
      height: getSizeValue(size.height),
      width: getSizeValue(size.width),
      ...(isStretched
        ? {
            docked: {
              left: { px: 0, vw: 0 },
              right: { px: 0, vw: 0 },
            },
          }
        : {}),
    },
    presetId: cleanPresetIdPrefix(preset.presetId),
    mobile: {
      id: cleanPresetIdPrefix(mobilePreset.presetId),
      size: {
        height: getSizeValue(mobilePresetSize.height),
        width: getSizeValue(mobilePresetSize.width),
      },
    },
  };
}

function getMobilePresetId(widgetDefinition: UnifiedWidget, presetId?: string) {
  const preset = widgetDefinition.presets?.find(
    (_preset) => _preset.id === presetId,
  );
  if (preset) {
    return preset.breakpoints?.small?.presetId ?? presetId;
  }

  // TODO: After Blocks migration to new presets schema, remove all code in next lines
  //  should only check the relevant preset in widgetDefinition.presets
  //  if small breakpoint has different preset ID return it, otherwise return the default/given preset ID - ask Product
  const presetEditorPresence: PresetEditorPresence =
    widgetDefinition.editorPresence?.presetsEditorPresence?.find(
      (_presetEditorPresence: PresetEditorPresence) =>
        _presetEditorPresence.presetId === presetId,
    );
  if (presetEditorPresence) {
    return presetEditorPresence.mobilePresetId ?? presetId;
  }

  const defaultUnifiedComponentPreset =
    widgetDefinition.installation?.widget?.defaultPreset;
  if (defaultUnifiedComponentPreset) {
    return defaultUnifiedComponentPreset.mobilePresetId ?? presetId;
  }

  const defaultBlocksInstallationPreset =
    widgetDefinition?.installationSettings?.mainPresets;
  if (defaultBlocksInstallationPreset) {
    return defaultBlocksInstallationPreset.mobilePresetId ?? presetId;
  }

  return presetId;
}

export function getWidgetAppearanceData(
  widgetDefinition: UnifiedWidget,
  widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
  presetId?: string,
  position: Point = { x: 0, y: 0 },
  isStretched: boolean = false,
) {
  const selectedPresetId =
    presetId ??
    widgetDefinition.installation?.widget?.defaultPreset?.desktopPresetId;

  const mobileDefaultPresetId =
    widgetDefinition.installation?.widget?.defaultPreset?.mobilePresetId ||
    selectedPresetId;
  const mobilePresetId = getMobilePresetId(
    widgetDefinition,
    mobileDefaultPresetId,
  );

  const {
    mobile,
    size,
    presetId: addedPresetId,
  } = getPresetAndSize(
    widgetDefinition.widgetId,
    isStretched,
    widgetDescriptor,
    selectedPresetId,
    mobilePresetId,
  );

  const layout = {
    ...size,
    ...position,
  };

  const layouts: Partial<ComponentLayoutObject> = {
    componentLayout: isStretched
      ? {
          type: 'ComponentLayout',
          height: { type: 'percentage', value: 100 },
          width: { type: 'percentage', value: 100 },
        }
      : {
          type: 'ComponentLayout',
          width: getUnitSize(size.width),
          height: { type: 'auto' },
          minWidth: getUnitSize(size.width),
          minHeight: getUnitSize(size.height),
        },
    containerLayout: {
      type: 'GridContainerLayout',
      rows: [{ type: 'fr', value: 1 }],
      columns: [{ type: 'fr', value: 1 }],
    },
    itemLayout: {
      id: '',
      type: 'GridItemLayout',
      gridArea: {
        columnStart: 1,
        columnEnd: 2,
        rowStart: 1,
        rowEnd: 2,
      },
      alignSelf: 'start',
      justifySelf: 'start',
      margins: {
        left: { type: 'percentage', value: 0 },
        top: { type: 'px', value: 0 },
      },
    },
    type: 'SingleLayoutData',
  };

  return {
    presetId: addedPresetId,
    size,
    mobile,
    layout,
    layouts,
  };
}

export function getBlocksStructure(
  context: ExtendedPlatformContext,
  appDefinitionId: string,
  widgetDefinition: UnifiedWidget,
  presetId: string,
  position: Point,
  widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
): CompStructure {
  const isStudio = window.commonConfig?.host?.toLowerCase() === 'studio';
  const widgetAppearanceData = getWidgetAppearanceData(
    widgetDefinition,
    widgetDescriptor,
    presetId,
    position,
  );
  const presets = {
    layout: widgetAppearanceData.presetId,
    style: widgetAppearanceData.presetId,
  };

  const findMobileBreakpointId = () => {
    const pageRef = context.documentServices.pages.getCurrentPage();
    const breakpoints =
      context.documentServices.breakpoints.get(pageRef).values;
    return breakpoints.reduce((prev, curr) =>
      prev.canvasSize! < curr.canvasSize! ? prev : curr,
    ).id!;
  };

  const scopedPresetsMobileKey = isStudio
    ? findMobileBreakpointId()
    : 'MOBILE-VARIANT';

  // TODO: [WBL-9453] returned type in DM for buildRefWidgetStructure is void
  // @ts-expect-error
  const structure: CompStructure =
    context.documentServices.appStudioWidgets.buildRefWidgetStructure(
      appDefinitionId,
      widgetDefinition.widgetId,
      {
        // TODO: [WBL-9453] type of options is not updated for buildRefWidgetStructure in DM
        // @ts-expect-error
        presets,
        scopedPresets: {
          [scopedPresetsMobileKey]: {
            layout: widgetAppearanceData.mobile.id,
            style: widgetAppearanceData.mobile.id,
          },
        },
        ...(isStudio
          ? { layouts: widgetAppearanceData.layouts }
          : {
              layout: widgetAppearanceData.layout,
              props: {
                type: 'RefComponentProperties',
                minHeight: widgetAppearanceData.layout.height,
              },
              mobileHints: {
                type: 'MobileHints',
                recommendedHeight: widgetAppearanceData.mobile.size.height,
                recommendedWidth: widgetAppearanceData.mobile.size.width,
                props: {
                  minHeight: widgetAppearanceData.mobile.size.height,
                  type: 'RefComponentProperties',
                },
              },
            }),
      },
    );

  if (structure.layouts) {
    structure.layoutResponsive = {
      id: 'FAKE',
      type: 'LayoutData',
      componentLayouts: [
        ...(structure.layouts.componentLayout
          ? [structure.layouts.componentLayout]
          : []),
      ],
      containerLayouts: [
        ...(structure.layouts.containerLayout
          ? [structure.layouts.containerLayout]
          : []),
      ],
      itemLayouts: [
        ...(structure.layouts.itemLayout ? [structure.layouts.itemLayout] : []),
      ],
    } as CompStructure['layoutResponsive'];
  }

  return structure;
}

async function addBlocksClosedWidget(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDescriptor: WidgetDescriptor,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const { widgetId } = widgetDefinition;
  const isStretched = !!widgetDefinition.isStretched?.desktop;
  const { size, presetId, mobile, layout, layouts } = getWidgetAppearanceData(
    widgetDefinition,
    widgetDescriptor,
    addToStageData.presetId,
    addToStageData.position,
    isStretched,
  );

  return new Promise<CompRef>((resolve, reject) =>
    // TODO: [EP-5207] Use getBlocksStructure function and replace API used to add blocks widgets to the
    //  general DM components API (context.platformApiMethods.document.components.add)
    context.platformApiMethods.application.appStudioWidgets
      .addWidget(appData, TOKEN, {
        widgetId,
        installationType: 'closed',
        presetIds: presetId
          ? {
              layout: presetId,
              style: presetId,
            }
          : undefined,
        layout,
        layouts,
        containerRef,
        scopedPresets: {
          mobile: {
            layout: mobile.id,
            style: mobile.id,
          },
        },
        dimensionsByDevice: {
          desktop: {
            height: getUnitSize(size.height),
            width: getUnitSize(size.width),
          },
          mobile: {
            height: getUnitSize(mobile.size.height),
            width: getUnitSize(mobile.size.width),
          },
        },
      })
      .then(resolve)
      .catch((error: Error) =>
        reject(
          createAddUnifiedComponentsError(
            ErrorCode.addingBlocksWidgetFailed,
            'Could not add blocks widget',
          )
            .withUserComponentId(widgetId, 'unified-widget')
            .withParentError(error),
        ),
      ),
  );
}

async function getWidgetDescriptor(
  appData: AppData,
  widgetDefinition: UnifiedWidget,
) {
  if (!appDescriptors[appData.appDefinitionId]) {
    appDescriptors[appData.appDefinitionId] = await getAppDescriptorByAppData(
      appData,
    );
  }
  const appDescriptor = appDescriptors[appData.appDefinitionId];
  const widgetDescriptor = Object.values(appDescriptor?.widgets ?? {}).find(
    ({ devCenterWidgetId }) => devCenterWidgetId === widgetDefinition.widgetId,
  );
  if (!widgetDescriptor) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBlocksWidgetFailed,
      'Could not fetch widget from app descriptor',
    ).withUserComponentId(widgetDefinition.widgetId, 'unified-widget');
  }
  return widgetDescriptor;
}

export async function addBlocksWidgetResolver(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const widgetDescriptor = await getWidgetDescriptor(appData, widgetDefinition);
  if (
    widgetDefinition.componentModel?.componentType &&
    isBuilderComponentType(widgetDefinition.componentModel.componentType)
  ) {
    return addBuilderComponent(
      context,
      appData,
      widgetDescriptor,
      widgetDefinition,
      containerRef,
      addToStageData,
    );
  }

  return addBlocksClosedWidget(
    context,
    appData,
    widgetDescriptor,
    widgetDefinition,
    containerRef,
    addToStageData,
  );
}
